import { useState, useEffect, useRef } from "react";
import { cn } from "tailwind-variants";
import Preview from "./Preview.vue";
import UseSvg from "common/vue/components/UseSvg.react.vue";
import { useDtfStore } from "../store";
import "./AddLetters.scss";
import { renderList } from "common/vue/render-list";

export default function AddLetters(props) {
  const { ...restProps } = props;
  const store = useDtfStore();
  const [textBoxes, setTextBoxes] = useState([{ value: "" }]);
  const [activeTextbox, setActiveTextbox] = useState(null);
  const timerRef = useRef(null);
  const prevStyleRef = useRef(null);
  const prevTextBoxesLengthRef = useRef(textBoxes.length);

  function letterNumberArtwork() {
    return store.letterNumberArtwork;
  }

  function letterPreviewMessages() {
    return store.letterPreviewMessages;
  }

  function inputStyles(index, input) {
    return cn(
      "add-letters__input",
      letterNumberArtwork().textIndex === index && !!input.value && "add-letters__input--selected"
    );
  }

  function setArtworkDefaultValues() {
    store.updateArtworkValues({
      index: 0,
      textIndex: 0,
      texts: []
    });
    store.generateLettersPreview();
  }

  function handleChange(index, event) {
    const { value } = event.target;
    setActiveTextbox(index);
    const textIndex = index;
    if (!value) {
      store.setLetterPreviewMessage({
        textIndex,
        message: null
      });
    }
    const texts = textBoxes.map(obj => obj.value);
    const quantity = texts.filter(Boolean).length;
    store.updateArtworkValues({
      index: 0,
      textIndex,
      texts,
      quantity
    });
    if (value) {
      store.generateLettersPreview();
    }
  }

  function triggerChange(index, event) {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      const { value } = event.target;
      if (value) {
        store.setLetterPreviewLoading(true);
      }
      handleChange(index, event);
    }, 500);
  }

  function handleInput(index, event) {
    const value = event.target.value.trim();
    if (value !== "") {
      const newTextBoxes = [...textBoxes];
      newTextBoxes[index].value = value;
      setTextBoxes(newTextBoxes);
    }
  }

  function removeTextBox(index) {
    if (textBoxes.length === 1) {
      setTextBoxes([{ value: "" }]);
      store.clearLetterPreviewMessages();
      setArtworkDefaultValues();
      return;
    }
    store.removeLetterPreviewMessage(index);
    const newTextBoxes = textBoxes.filter((_, i) => i !== index);
    setTextBoxes(newTextBoxes);
    let textIndex = index;
    const currentIndex = letterNumberArtwork().textIndex;
    let texts = newTextBoxes.map(obj => obj.value);
    if (textIndex < currentIndex) {
      textIndex = currentIndex - 1;
    }
    if (!texts[textIndex]) {
      const reversedTexts = [...texts].reverse();
      textIndex = texts.length - 1 - reversedTexts.findIndex(Boolean);
    }
    if (newTextBoxes.length === 1 && !newTextBoxes[0].value) {
      texts = [];
    }
    const quantity = texts.filter(Boolean).length;
    store.updateArtworkValues({
      index: 0,
      textIndex,
      texts,
      quantity
    });
    store.generateLettersPreview();
  }

  function handleFocus(index) {
    const textBox = textBoxes[index];
    setActiveTextbox(index);
    if (!textBox.value) return;
    store.updateArtworkValues({
      index: 0,
      textIndex: index
    });
    store.generateLettersPreview();
  }

  function addTextBox() {
    const currentIndex = textBoxes.length - 1;
    if (!textBoxes[currentIndex].value) return;
    const newTextBoxes = [...textBoxes, { value: "" }];
    setTextBoxes(newTextBoxes);
    const texts = newTextBoxes.map(obj => obj.value);
    store.updateArtworkValues({
      index: 0,
      textIndex: currentIndex,
      texts
    });
    store.generateLettersPreview({
      useDebounce: false
    });
  }

  function handleBlur(index) {
    setActiveTextbox(index);
    const texts = textBoxes.map(obj => obj.value);
    const quantity = texts.filter(Boolean).length;
    store.updateArtworkValues({
      index: 0,
      texts,
      quantity
    });
  }

  useEffect(() => {
    const newValue = letterNumberArtwork().style;
    const oldValue = prevStyleRef.current;
    if (oldValue && newValue === null) {
      setTextBoxes([{ value: "" }]);
      store.clearLetterPreviewMessages();
    }
    prevStyleRef.current = newValue;
  }, [JSON.stringify(letterNumberArtwork().style)]);

  useEffect(() => {
    const prevLength = prevTextBoxesLengthRef.current;
    const currentLength = textBoxes.length;
    prevTextBoxesLengthRef.current = currentLength;
    if (currentLength > prevLength) {
      const newIndex = currentLength - 1;
      queueMicrotask(() => {
        const newTextbox = document.getElementById(`textbox-${newIndex}`);
        if (newTextbox) {
          newTextbox.focus();
        }
      });
    }
  }, [textBoxes.length]);

  return (
    <div {...restProps} className={cn("add-letters", restProps.className)}>
      <div className="add-letters__content">
        {renderList(textBoxes, (input, index) => (
          <div className="add-letters__name-wrapper" key={index}>
            <div className="add-letters__input-wrapper">
              <input
                className={cn(inputStyles(index, input))}
                id={`textbox-${index}`}
                value={input.value}
                onInput={e => {
                  const newTextBoxes = [...textBoxes];
                  newTextBoxes[index].value = e.target.value.trim();
                  setTextBoxes(newTextBoxes);
                  handleInput(index, e);
                }}
                type="text"
                onFocus={() => {
                  handleFocus(index);
                }}
                onBlur={() => {
                  handleBlur(index);
                }}
                onKeyDown={e => {
                  if (e.key !== "Enter") return;
                  e.preventDefault();
                  addTextBox(e);
                }}
                onKeyUp={e => {
                  triggerChange(index, e);
                }}
                data-testid="add-letters-input"
              />
              {letterPreviewMessages()[index] ? (
                <div className="add-letters__error">{letterPreviewMessages()[index]}</div>
              ) : null}
            </div>
            <button
              className="add-letters__button add-letters__button--remove"
              type="button"
              onClick={() => {
                removeTextBox(index);
              }}
              data-testid="add-letters-button"
            >
              <UseSvg
                className="add-letters__remove-icon"
                icon="modal-close"
                data-testid="add-letters-remove-icon"
              />
            </button>
          </div>
        ))}
        <div className="add-letters__new-name">
          <button
            className="add-letters__button"
            type="button"
            onClick={addTextBox}
            data-testid="add-letters-button-1"
          >
            {" "}
            + Add another name{" "}
          </button>
        </div>
      </div>
      <div className="add-letters__content">
        <Preview data-testid="preview" />
      </div>
    </div>
  );
}
